




































import Vue from 'vue'
import SurfacePopupWithTip from 'vuecomponents/surface_popup_with_tip.vue'
import SurfacePost from 'vuecomponents/surface_post.vue'
import { Prop, Component, Watch, Emit } from 'vue-property-decorator'
import { PostAttributes, PinDropSuggestion } from 'types'
import {
  getLocationNameSuggestionsForMap,
  Marker,
  GeoMap,
  OverlappingMarkerSpiderfier,
  MapEventsManager,
  Point,
  Size,
  Geocoder,
  GeocoderResult,
  GeocoderStatus,
  LatLng,
} from 'bits/map'
import CustomPopup from 'bits/map_popup'
import { namespace, State, Getter } from 'vuex-class'
import { processedUrl } from '@padlet/vivaldi-client'

const MARKER_ICON = {
  anchor: new Point(18, 36), // y = 36 is the bottom of the tip (there is a 12px padding below for the elliptic shadow)
  scaledSize: new Size(36, 48),
  labelOrigin: new Point(18, 14),
}

@Component({
  components: { SurfacePopupWithTip, SurfacePost },
})
class SurfaceMapGoogleMarker extends Vue {
  $refs!: {
    popupContent: HTMLDivElement
  }

  $slots!: {
    default
  }

  marker!: Marker
  popup!: CustomPopup
  geocodingResults?: GeocoderResult[] | null = null
  pinDropSuggestions?: PinDropSuggestion[] | null = null
  openedOnce = false
  scrolledAfterOpened = false
  scrolledAfterOpenedListener?: google.maps.MapsEventListener
  computedFillColor = 'FFF'
  computedStrokeColor = '000'
  computedZIndex = 200
  // listener
  @State inlinePostEdit
  @Getter xHeader!: boolean
  @State dpr!: number
  @State user
  @Getter canIModerate!: boolean
  @Getter canIWrite!: boolean
  @namespace('map').Getter offsetForMapPopup!: any
  @namespace('post').Getter postBeingEditedCid
  @namespace('map').Getter overlappingMarkerSpiderfier?: OverlappingMarkerSpiderfier
  @namespace('map').Getter markerClusterer?: any | null
  @Prop({ type: Object, required: false }) map!: GeoMap
  @Prop({ type: Number, required: false }) zIndex!: number
  @Prop({ type: String, required: true }) fillColor!: string
  @Prop({ type: String, required: true }) strokeColor!: string
  @Prop({ type: Object, required: false }) markerShape!: google.maps.MarkerShape
  @Prop({ type: Boolean, required: false, default: true }) allowingGrouping!: boolean
  @Prop({ type: Boolean, required: false, default: false }) highlighted!: boolean
  @Prop({ type: Object, required: true }) position?: { lat: number; lng: number }
  @Prop({ type: Object, required: false }) post!: PostAttributes | null
  @Prop({ type: String, required: false }) label!: string
  @Prop({ type: Boolean, required: false }) opened!: boolean
  @namespace('map').Action disableMapFittingMarkersBounds
  @namespace('map').Action pickLocation
  @namespace('attachment').Getter linkDisplayAttributesForUrl
  @namespace('attachment').Getter isSurfacePreviewImageLoadedForUrl!: (url) => boolean

  get postAttachmentImage(): string | null {
    const attachment = this.post ? this.post.attachment : null
    if (!attachment) return null
    const displayAttributes = this.linkDisplayAttributesForUrl(attachment)
    return displayAttributes ? displayAttributes.image_url : null || null
  }

  get isPostAttachmentImageLoaded(): boolean {
    return this.post?.attachment ? this.isSurfacePreviewImageLoadedForUrl(this.post.attachment) : false
  }

  get locationName(): string | undefined {
    return this.post ? this.post.location_name : undefined
  }

  @Watch('locationName')
  respondToLocationNameChanged(locationName) {
    if (locationName) {
      this.geocodingResults = null
      this.pinDropSuggestions = null
    }
  }

  @Watch('postAttachmentImage')
  respondToPostAttachmentLoaded(postAttachmentImage) {
    if (!postAttachmentImage) return
    if (!this.opened) return
    if (!this.scrolledAfterOpened) {
      this.$nextTick(() => {
        // Use next tick so that the img tag finished render before we pan
        this.panMapToFitPost()
      })
    }
    this.scrolledAfterOpenedListener && this.scrolledAfterOpenedListener.remove()
    this.scrolledAfterOpenedListener = undefined
  }

  @Watch('isPostAttachmentImageLoaded')
  respondToPostAttachmentImageLoaded(isImageLoaded: boolean): void {
    if (!isImageLoaded) return
    if (!this.opened) return
    if (!this.scrolledAfterOpened) {
      this.$nextTick(() => {
        // Use next tick so that the img tag finished render before we pan
        this.panMapToFitPost()
      })
    }
    this.scrolledAfterOpenedListener && this.scrolledAfterOpenedListener.remove()
    this.scrolledAfterOpenedListener = undefined
  }

  @Watch('zIndex')
  respondToZIndexChange(zIndex) {
    this.computedZIndex = zIndex
  }

  @Watch('computedZIndex')
  respondToComputedZIndexChange(zIndex) {
    this.marker && this.marker.setZIndex(zIndex)
  }

  createMarker() {
    // const marker = new CustomMarker(latLng, this.map, label, { markerId: postCid })
    this.marker = new Marker({
      position: this.position,
      map: undefined,
      shape: this.markerShape,
      zIndex: this.zIndex,
    })
    this.changeMarkerLabel(this.label, this.computedStrokeColor)
    this.showMarker()
  }

  createPopup() {
    if (this.post || (this.$slots.default && this.$slots.default.length > 0)) {
      this.popup = new CustomPopup({
        position: this.position,
        map: null,
      })
    }
  }

  panMapToFitPost(post: Element = this.$refs.popupContent /*, allowZoomToFit = true */) {
    const boundingRect = post.getBoundingClientRect() as DOMRect
    if (boundingRect.x === 0 && boundingRect.y === 0) {
      setTimeout(() => {
        this.panMapToFitPost(post)
      }, 0)
      return
    }
    const MIN_OFFSET_TOP = this.offsetForMapPopup.top
    const MIN_OFFSET_BOTTOM = this.offsetForMapPopup.bottom
    const MIN_OFFSET_LEFT = this.offsetForMapPopup.left
    const MIN_OFFSET_RIGHT = this.offsetForMapPopup.right
    let moveDistance = 0
    moveDistance = MIN_OFFSET_TOP - boundingRect.top
    // Start of panning logic
    // Get the pixel of the current center
    const center = this.map.getCenter()
    const centerPixel = this.popup.fromLatLngToPixel(center)
    if (this.map && centerPixel) {
      // If the top edge of the post is outside the map
      if (boundingRect.top < MIN_OFFSET_TOP) {
        // Move the map by the calculated pixels above
        centerPixel.y -= moveDistance
      }
      // If the left edge of the post is outside the map
      if (boundingRect.left < 0) {
        // Move the map to the right
        centerPixel.x += boundingRect.left - MIN_OFFSET_LEFT
      }
      // If the right edge of the post is outside the map
      if (boundingRect.right > window.innerWidth) {
        // Move the map to the left
        centerPixel.x -= window.innerWidth - boundingRect.right - MIN_OFFSET_RIGHT
      }
      // If the bottom edge of the post is outside the map (result of zooming and panning)
      if (boundingRect.bottom + MIN_OFFSET_BOTTOM > window.innerHeight) {
        centerPixel.y -= window.innerHeight - boundingRect.bottom - MIN_OFFSET_BOTTOM // 60 to save space for the marker
      }
      const newCenter = this.popup.fromPixelToLatLng(centerPixel)
      if (newCenter && !newCenter.equals(center)) {
        this.map.panTo(newCenter)
        // N.B. If we are restricting the bounds of the map, we will need these lines of code below.
        // if (allowZoomToFit) {
        //   MapEventsManager.addListenerOnce(this.map, 'idle', () => {
        //   // If the post is still out of the map, it means the current
        //   // zoom level prevent the map from panning to show all of the post
        //   // we need to zoom in and redo the panning
        //     const newBoundingRect = post.getBoundingClientRect()
        //     if (Math.ceil(newBoundingRect.top) < MIN_OFFSET_TOP) {
        //     // Zoom at least 1 higher, depends on how much of the post is still hidden
        //       this.map.setZoom(this.map.getZoom() + Math.ceil((MIN_OFFSET_TOP - newBoundingRect.top) / 200))
        //       // Redoing the panning
        //       MapEventsManager.addListenerOnce(this.map, 'idle', () => {
        //         this.panMapToFitPost(post, false)
        //       })
        //     }
        //   })
        // }
      }
    }
  }

  centerMapToPostBeingEdited() {
    this.$nextTick(() => {
      this.centerMapToPost()
    })
    // When user searches for and adds a new pin, the map pans so that the post is visible but not centered.
    // To center the post, we have to wait for the first pan to complete.
    MapEventsManager.addListenerOnce(this.map, 'idle', () => {
      this.centerMapToPost()
    })
  }

  centerMapToPost() {
    if (!this.position) {
      return
    }
    const newCenterPixel = this.popup.fromLatLngLiteralToPixel(this.position)
    if (!newCenterPixel) {
      return
    }
    newCenterPixel.y -= this.$refs.popupContent.getBoundingClientRect().height / 2 + this.offsetForMapPopup.bottom
    const newCenter = this.popup.fromPixelToLatLng(newCenterPixel)
    newCenter && this.map.panTo(newCenter)
  }

  changeMarkerLabel(label, labelColor) {
    if (label) {
      this.marker.setLabel({
        text: label || '',
        color: '#' + labelColor,
      })
    } else {
      this.marker.setLabel(null)
    }
    this.setMarkerIcon()
  }

  @Watch('map')
  onMapUpdated(map) {
    this.showMarker()
    if (this.popup.isOpened) {
      this.popup.setMap(map)
    } else {
      this.popup.setMap(null)
    }
  }

  setMarkerIcon() {
    this.marker.setIcon({
      ...MARKER_ICON,
      url: processedUrl(
        `https://padlet.com/static/v1/map-marker.svg?fill=${this.computedFillColor}&stroke=${
          this.computedStrokeColor
        }&dot=${this.marker.getLabel() ? 0 : 1}`,
        {
          width: 36,
          height: 48,
          dpr: this.dpr,
        },
      ),
    })
  }

  @Watch('strokeColor', { immediate: true })
  onStrokeColorChanged(strokeColor) {
    this.computedStrokeColor = strokeColor
  }

  @Watch('fillColor', { immediate: true })
  onFillColorChanged(fillColor) {
    this.computedFillColor = fillColor
  }

  @Watch('computedStrokeColor')
  @Watch('computedFillColor')
  onIconColorChanged() {
    this.setMarkerIcon()
    const currentLabel = this.marker.getLabel()
    if (currentLabel) {
      this.changeMarkerLabel(currentLabel.text, this.computedStrokeColor)
    }
  }

  @Watch('highlighted')
  respondToHighlightedChange(highlighted) {
    if (highlighted) {
      this.computedFillColor = this.strokeColor
      this.computedStrokeColor = this.fillColor
      this.computedZIndex = 9999
    } else {
      this.computedFillColor = this.fillColor
      this.computedStrokeColor = this.strokeColor
      this.computedZIndex = this.zIndex
    }
  }

  @Watch('label')
  onLabelUpdated(label) {
    this.changeMarkerLabel(label, this.computedStrokeColor)
  }

  @Watch('markerShape')
  onMarkerShapeChanged(markerShape) {
    this.marker.setShape(markerShape)
  }

  @Watch('position')
  onPositionUpdated(position) {
    this.marker.setPosition(position)
    this.popup.setPosition(position)
  }

  @Watch('opened')
  onOpenedChanged(opened: boolean) {
    if (this.location && this.geocodingResults)
      this.pinDropSuggestions = getLocationNameSuggestionsForMap(this.location, this.geocodingResults, this.map)
    this.checkAndOpenPopup(opened)
    if (this.post && this.post.attachment) {
      this.scrolledAfterOpened = false
      this.scrolledAfterOpenedListener = MapEventsManager.addDomListenerOnce(this.map, 'drag', () => {
        this.scrolledAfterOpened = true
      })
    }
  }

  get isCurrentPostBeingEdited(): boolean {
    return (this.post && this.post.cid === this.postBeingEditedCid) || false
  }

  @Watch('isCurrentPostBeingEdited')
  respondToIsCurrentPostBeingEdited(editing) {
    if (editing) {
      this.centerMapToPostBeingEdited()
    }
  }

  showMarker() {
    if (this.allowingGrouping) {
      this.overlappingMarkerSpiderfier &&
        this.overlappingMarkerSpiderfier.trackMarker(this.marker, null as unknown as Function)
      if (this.markerClusterer) {
        this.markerClusterer.addMarker(this.marker)
      } else {
        this.marker.setMap(this.map)
      }
    } else {
      this.marker.setMap(this.map)
    }
  }

  removeMarkerFromClusterer() {
    this.markerClusterer && this.markerClusterer.removeMarker(this.marker)
    this.marker.setMap(this.map)
  }

  addMarkerToClusterer() {
    this.marker.setMap(null)
    this.markerClusterer && this.markerClusterer.addMarker(this.marker)
  }

  checkAndOpenPopup(opened: boolean) {
    if (opened && !this.openedOnce) {
      this.openedOnce = true
      this.$nextTick(() => {
        this.popup.setContent(this.$refs.popupContent)
      })
    }
    if (opened && !this.popup.isOpened) {
      // this.removeMarkerFromClusterer()
      this.disableMapFittingMarkersBounds()
      this.popup.open(this.map)
      if (this.isCurrentPostBeingEdited) {
        this.centerMapToPostBeingEdited()
      }
      this.$nextTick(() => {
        if (!this.isCurrentPostBeingEdited) {
          this.panMapToFitPost()
        }
      })
    } else if (!opened && this.popup.isOpened) {
      // this.addMarkerToClusterer()
      this.popup.close()
    }
  }

  @Emit('open-marker-popup')
  openMarkerPopup(id) {
    return id
  }

  @Emit('close-marker-popup')
  closeMarkerPopup(id) {
    return id
  }

  get wasWrittenByCurrentUser(): boolean {
    if (!this.post) return false
    return !!this.user.id && this.post.author_id === this.user.id
  }
  // Either the current user is a moderator or current user is the author and wall is writable
  get isEditableByCurrentUser(): boolean {
    return this.canIModerate || (this.wasWrittenByCurrentUser && this.canIWrite)
  }

  get location(): LatLng | null {
    if (!this.post || !this.post.location_point) return null
    return new LatLng({ lat: this.post.location_point.latitude, lng: this.post.location_point.longitude })
  }

  mounted() {
    if (
      this.post &&
      this.post.location_point &&
      this.location &&
      !this.post.location_name &&
      this.isEditableByCurrentUser
    ) {
      // Get list of location names
      const location = this.location
      const geocoder = new Geocoder()
      geocoder &&
        geocoder.geocode(
          {
            location,
          },
          (results, status) => {
            if (status === GeocoderStatus.OK) {
              this.geocodingResults = results
              const endResults = getLocationNameSuggestionsForMap(location, results, this.map)
              this.pinDropSuggestions = endResults
            } else {
              this.pinDropSuggestions = null
            }
          },
        )
    }
    this.createMarker()
    this.marker.addListener('spider_click', () => {
      // Trigger event to open popup from parent component
      if (!this.opened) {
        this.post && this.openMarkerPopup(this.post.cid)
      } else {
        this.post && this.closeMarkerPopup(this.post.cid)
      }
    })
    this.marker.addListener('spider_format', (status) => {
      if (!this.overlappingMarkerSpiderfier) return
      if (
        status === OverlappingMarkerSpiderfier.markerStatus.SPIDERFIED ||
        status === OverlappingMarkerSpiderfier.markerStatus.UNSPIDERFIABLE
      ) {
        this.changeMarkerLabel(null, this.computedStrokeColor)
      }
      if (status === OverlappingMarkerSpiderfier.markerStatus.SPIDERFIABLE) {
        try {
          const count = this.overlappingMarkerSpiderfier.markersNearMarker(this.marker).length + 1 // include current marker
          this.changeMarkerLabel(String(count), this.computedStrokeColor)
        } catch {
          // do nothing
          /**
           * #WONTFIX
           * ignoring "Must wait for 'idle' event on map before calling markersNearMarker" that unknowningly happen very rarely.
           * Silencing this Sentry error: https://sentry.io/organizations/padlet-y9/issues/1202028855/?project=22080
           * This error makes marker's label not showing the number of markers when loaded, but can still be clicked and behave
           * normally. However if user just zoom in/out of the map the number would appear again.
           */
        }
      }
    })
    this.marker.addListener('mouseover', () => {
      this.computedFillColor = this.strokeColor
      this.computedStrokeColor = this.fillColor
      this.computedZIndex = 999
    })
    this.marker.addListener('mouseout', () => {
      ;[this.computedFillColor, this.computedStrokeColor] = [this.fillColor, this.strokeColor]
      this.computedZIndex = this.zIndex
    })
    this.createPopup()
    this.checkAndOpenPopup(this.opened)
  }

  beforeDestroy() {
    if (this.marker) {
      this.marker.setMap(null)
      this.overlappingMarkerSpiderfier && this.overlappingMarkerSpiderfier.forgetMarker(this.marker)
      this.markerClusterer && this.markerClusterer.removeMarker(this.marker)
    }
    if (this.popup) {
      this.popup.setMap(null)
    }
  }
}
export default SurfaceMapGoogleMarker
